import React, { useState, useEffect } from 'react';
import { 
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  PieChart as PieChartIcon, BarChart3, TrendingUp, Calendar, 
  ArrowLeft, Download, RefreshCw, DollarSign, FileText, 
  CheckCircle, XCircle, Building2, Clock, Users, Database,
  Activity, Target, Shield, Zap, Mail, MousePointer, Eye,
  FlaskConical, Trophy, Percent, Lock, Unlock, RotateCcw, 
  AlertTriangle, CheckCircle2, XOctagon, Pause, Play
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import { api, apiFetch } from '../utils/api';
import ExtractionAnalytics from '../components/analytics/ExtractionAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color palettes
const STATUS_COLORS: Record<string, string> = {
  completed: '#22c55e',
  pending: '#f59e0b',
  draft: '#94a3b8',
  failed: '#ef4444',
  unknown: '#6b7280'
};

const EVENT_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#84cc16'];
const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Health score colors
const HEALTH_COLORS = {
  green: '#22c55e',
  yellow: '#f59e0b', 
  red: '#ef4444'
};

// Calculation mode colors
const MODE_COLORS = {
  historical: '#3b82f6',
  future_closure: '#8b5cf6'
};

// Data source colors
const SOURCE_COLORS = {
  actual: '#22c55e',
  bls_average: '#f59e0b',
  mixed: '#3b82f6'
};

interface AnalyticsData {
  summary: {
    total_claims: number;
    total_value: number;
    average_value: number;
    completed_count: number;
    pending_count: number;
    recent_claims_7d: number;
    avg_completion_days?: number;
  };
  status_breakdown: Array<{ status: string; count: number }>;
  event_type_breakdown: Array<{ event_type: string; count: number; total_value: number }>;
  business_type_breakdown: Array<{ business_type: string; count: number }>;
  causation_results: {
    passed: number;
    failed: number;
    success_rate: number;
  };
  monthly_trends: Array<{ month: string; claims: number; value: number }>;
  top_claims: Array<{
    id: string;
    client_name: string;
    event_type: string;
    value: number;
    status: string;
  }>;
  // NEW ANALYTICS
  health_score_distribution?: { green: number; yellow: number; red: number };
  calculation_mode_split?: { historical: number; future_closure: number };
  data_source_usage?: { actual: number; bls_average: number; mixed: number };
  top_clients?: Array<{ client_name: string; count: number }>;
  claim_value_distribution?: Array<{ bucket: string; count: number }>;
  monte_carlo_aggregate?: {
    claims_with_mc: number;
    avg_ci_width_pct: number;
    avg_p50: number;
    total_ci_low: number;
    total_ci_high: number;
  };
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  
  // Vendor Classification History
  const [vendorHistory, setVendorHistory] = useState<any>(null);
  const [vendorHistoryLoading, setVendorHistoryLoading] = useState(false);
  
  // Email Engagement Analytics
  const [emailAnalytics, setEmailAnalytics] = useState<any>(null);
  const [emailAnalyticsLoading, setEmailAnalyticsLoading] = useState(false);
  
  // A/B Test Results
  const [abTestResults, setAbTestResults] = useState<any>(null);
  const [abTestLoading, setAbTestLoading] = useState(false);
  const [lockedWinners, setLockedWinners] = useState<any>({});
  const [applyingWinner, setApplyingWinner] = useState<string | null>(null);
  
  // Email Retry Queue
  const [retryStats, setRetryStats] = useState<any>(null);
  const [retryHistory, setRetryHistory] = useState<any[]>([]);
  const [retryLoading, setRetryLoading] = useState(false);
  const [processingRetries, setProcessingRetries] = useState(false);
  const [cancellingRetry, setCancellingRetry] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      
      const result = await api.get('/api/v1/analytics/summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchVendorHistory = async () => {
    try {
      setVendorHistoryLoading(true);
      const token = await getToken();
      
      const result = await api.get('/api/v1/vendor-intelligence/classification-history?months=6', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorHistory(result);
    } catch (err: any) {
      console.error('Failed to fetch vendor history:', err);
    } finally {
      setVendorHistoryLoading(false);
    }
  };
  
  const fetchEmailAnalytics = async () => {
    try {
      setEmailAnalyticsLoading(true);
      const token = await getToken();
      
      // Fetch aggregated email analytics across all reports
      const result = await api.get('/api/v1/analytics/email-engagement', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailAnalytics(result);
    } catch (err: any) {
      console.error('Failed to fetch email analytics:', err);
    } finally {
      setEmailAnalyticsLoading(false);
    }
  };
  
  const fetchAbTestResults = async () => {
    try {
      setAbTestLoading(true);
      const token = await getToken();
      
      // Fetch both results and locked winners in parallel
      const [resultsRes, winnersRes] = await Promise.all([
        api.get('/api/v1/analytics/ab-test-results?email_type=evidence_request', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/v1/analytics/ab-test/locked-winners?email_type=evidence_request', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAbTestResults(resultsRes);
      setLockedWinners(winnersRes?.locked_winners || {});
    } catch (err: any) {
      console.error('Failed to fetch A/B test results:', err);
    } finally {
      setAbTestLoading(false);
    }
  };
  
  const applyWinner = async (testKey: string, variantId: string) => {
    try {
      setApplyingWinner(`${testKey}_${variantId}`);
      const token = await getToken();
      
      await api.post('/api/v1/analytics/ab-test/apply-winner', {
        email_type: 'evidence_request',
        test_key: testKey,
        variant_id: variantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh data
      await fetchAbTestResults();
    } catch (err: any) {
      console.error('Failed to apply winner:', err);
    } finally {
      setApplyingWinner(null);
    }
  };
  
  const clearWinner = async (testKey: string) => {
    try {
      setApplyingWinner(`clear_${testKey}`);
      const token = await getToken();
      
      await api.post('/api/v1/analytics/ab-test/clear-winner', {
        email_type: 'evidence_request',
        test_key: testKey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh data
      await fetchAbTestResults();
    } catch (err: any) {
      console.error('Failed to clear winner:', err);
    } finally {
      setApplyingWinner(null);
    }
  };
  
  const fetchRetryStats = async () => {
    try {
      setRetryLoading(true);
      const token = await getToken();
      
      // Fetch both stats and history in parallel
      const [statsRes, historyRes] = await Promise.all([
        api.get('/api/v1/analytics/email-retry/stats', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/v1/analytics/email-retry/history?limit=10', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setRetryStats(statsRes?.retry_stats || null);
      setRetryHistory(historyRes?.jobs || []);
    } catch (err: any) {
      console.error('Failed to fetch retry stats:', err);
    } finally {
      setRetryLoading(false);
    }
  };
  
  const processRetries = async () => {
    try {
      setProcessingRetries(true);
      const token = await getToken();
      
      await api.post('/api/v1/analytics/email-retry/process-pending', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh data after processing
      await fetchRetryStats();
    } catch (err: any) {
      console.error('Failed to process retries:', err);
    } finally {
      setProcessingRetries(false);
    }
  };
  
  const cancelRetry = async (jobId: string) => {
    try {
      setCancellingRetry(jobId);
      const token = await getToken();
      
      await api.post(`/api/v1/analytics/email-retry/${jobId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh data
      await fetchRetryStats();
    } catch (err: any) {
      console.error('Failed to cancel retry:', err);
    } finally {
      setCancellingRetry(null);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchVendorHistory();
    fetchEmailAnalytics();
    fetchAbTestResults();
    fetchRetryStats();
  }, []);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const token = await getToken();
      
      const blob = await apiFetch<Blob>('/api/v1/export/csv', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claims_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      // Error toast handled by api utility
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const token = await getToken();
      
      const blob = await apiFetch<Blob>('/api/v1/export/excel', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claims_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      // Error toast handled by api utility
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800 mb-2">Failed to Load Analytics</h2>
            <p className="text-red-600 mb-4">{error || 'Unknown error'}</p>
            <button 
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/50 rounded-lg transition"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-gray-600">Business Intelligence & Performance Metrics</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'CSV'}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Excel'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">Total Claims</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{data.summary.total_claims}</p>
            <p className="text-xs text-gray-500 mt-1">
              +{data.summary.recent_claims_7d} this week
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">Total Value</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(data.summary.total_value)}</p>
            <p className="text-xs text-gray-500 mt-1">
              Avg: {formatCurrency(data.summary.average_value)}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm text-gray-600">Completed</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{data.summary.completed_count}</p>
            <p className="text-xs text-gray-500 mt-1">
              {data.summary.pending_count} pending
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-gray-600">Causation Pass Rate</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{data.causation_results.success_rate}%</p>
            <p className="text-xs text-gray-500 mt-1">
              {data.causation_results.passed} passed / {data.causation_results.failed} failed causation tests
            </p>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Monthly Trends */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Monthly Trends
            </h3>
            {data.monthly_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.monthly_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      return `${month}/${year.slice(2)}`;
                    }}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      name === 'value' ? formatCurrency(value) : value,
                      name === 'value' ? 'Total Value' : 'Claims'
                    ]}
                  />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="claims" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="Claims"
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="value" 
                    stroke="#22c55e" 
                    strokeWidth={2}
                    name="Value"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No trend data available
              </div>
            )}
          </div>

          {/* Event Type Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-purple-600" />
              Claims by Event Type
            </h3>
            {data.event_type_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={data.event_type_breakdown}
                    dataKey="count"
                    nameKey="event_type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ event_type, percent }) => 
                      `${event_type} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={false}
                  >
                    {data.event_type_breakdown.map((_, index) => (
                      <Cell key={index} fill={EVENT_COLORS[index % EVENT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value, 'Claims']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No event type data
              </div>
            )}
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Status Breakdown */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Claims by Status
            </h3>
            {data.status_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.status_breakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis 
                    type="category" 
                    dataKey="status" 
                    tick={{ fontSize: 12 }}
                    width={80}
                  />
                  <Tooltip />
                  <Bar dataKey="count" name="Claims">
                    {data.status_breakdown.map((entry, index) => (
                      <Cell 
                        key={index} 
                        fill={STATUS_COLORS[entry.status] || STATUS_COLORS.unknown} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No status data
              </div>
            )}
          </div>

          {/* Business Type */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-600" />
              Claims by Business Type
            </h3>
            {data.business_type_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.business_type_breakdown.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="business_type" 
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Claims" fill="#f97316" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No business type data
              </div>
            )}
          </div>
        </div>

        {/* Top Claims Table */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Top Claims by Value
          </h3>
          {data.top_claims.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Event Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Claim Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_claims.map((claim, index) => (
                    <tr 
                      key={claim.id} 
                      className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/report/${claim.id}`)}
                    >
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">{claim.client_name}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600">{claim.event_type}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: `${STATUS_COLORS[claim.status] || STATUS_COLORS.unknown}20`,
                            color: STATUS_COLORS[claim.status] || STATUS_COLORS.unknown
                          }}
                        >
                          {claim.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-semibold text-green-600">
                          {formatCurrency(claim.value)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No claims data available
            </div>
          )}
        </div>

        {/* Event Value Breakdown */}
        <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Total Value by Event Type
          </h3>
          {data.event_type_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.event_type_breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="event_type" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`} />
                <Tooltip formatter={(value: number) => [formatCurrency(value), 'Total Value']} />
                <Bar dataKey="total_value" name="Total Value">
                  {data.event_type_breakdown.map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No value data available
            </div>
          )}
        </div>

        {/* NEW ANALYTICS SECTION */}
        <div className="mt-8 mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-purple-600" />
            Advanced Analytics
          </h2>
          <p className="text-gray-500 text-sm mt-1">Deep insights into claim processing and data quality</p>
        </div>

        {/* Row 1: Health Score + Avg Completion Time + Calculation Mode Split */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          {/* Health Score Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Claim Health Distribution
            </h3>
            {data.health_score_distribution ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Ready', value: data.health_score_distribution.green, color: HEALTH_COLORS.green },
                        { name: 'Needs Review', value: data.health_score_distribution.yellow, color: HEALTH_COLORS.yellow },
                        { name: 'Issues', value: data.health_score_distribution.red, color: HEALTH_COLORS.red }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {[HEALTH_COLORS.green, HEALTH_COLORS.yellow, HEALTH_COLORS.red].map((color, index) => (
                        <Cell key={index} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Ready ({data.health_score_distribution.green})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                    Review ({data.health_score_distribution.yellow})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    Issues ({data.health_score_distribution.red})
                  </span>
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-500">
                No health data available
              </div>
            )}
          </div>

          {/* Average Time to Completion */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Processing Efficiency
            </h3>
            <div className="flex flex-col items-center justify-center h-[200px]">
              <div className="text-5xl font-bold text-blue-600">
                {data.summary.avg_completion_days || 0}
              </div>
              <div className="text-gray-500 mt-2">Average Days to Complete</div>
              <div className="mt-4 grid grid-cols-2 gap-4 w-full text-center">
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-lg font-bold text-blue-700">{data.summary.completed_count}</div>
                  <div className="text-xs text-blue-600">Completed</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <div className="text-lg font-bold text-amber-700">{data.summary.pending_count}</div>
                  <div className="text-xs text-amber-600">In Progress</div>
                </div>
              </div>
            </div>
          </div>

          {/* Calculation Mode Split */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Historical vs Future Closure
            </h3>
            {data.calculation_mode_split ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Historical BI', value: data.calculation_mode_split.historical, color: MODE_COLORS.historical },
                        { name: 'Future Closure', value: data.calculation_mode_split.future_closure, color: MODE_COLORS.future_closure }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      <Cell fill={MODE_COLORS.historical} />
                      <Cell fill={MODE_COLORS.future_closure} />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    Historical ({data.calculation_mode_split.historical})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                    Future ({data.calculation_mode_split.future_closure})
                  </span>
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-500">
                No mode data available
              </div>
            )}
          </div>
        </div>

        {/* Row 2: BLS vs Actual + Top Clients */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* BLS vs Actual Data Usage */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-amber-600" />
              Data Source Usage
            </h3>
            {data.data_source_usage ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart 
                    data={[
                      { name: 'Verified Actual', count: data.data_source_usage.actual, fill: SOURCE_COLORS.actual },
                      { name: 'BLS Averages', count: data.data_source_usage.bls_average, fill: SOURCE_COLORS.bls_average },
                      { name: 'Mixed Sources', count: data.data_source_usage.mixed, fill: SOURCE_COLORS.mixed }
                    ]}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="count" name="Claims">
                      <Cell fill={SOURCE_COLORS.actual} />
                      <Cell fill={SOURCE_COLORS.bls_average} />
                      <Cell fill={SOURCE_COLORS.mixed} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-2 text-xs text-gray-500 text-center">
                  Claims using verified data have higher defensibility
                </div>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No source data available
              </div>
            )}
          </div>

          {/* Top Clients/Law Firms */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Top Clients by Volume
            </h3>
            {data.top_clients && data.top_clients.length > 0 ? (
              <div className="space-y-3 max-h-[280px] overflow-y-auto">
                {data.top_clients.map((client, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-900 truncate max-w-[200px]">{client.client_name}</span>
                    </div>
                    <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {client.count} claims
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-gray-500">
                No client data available
              </div>
            )}
          </div>
        </div>

        {/* Row 3: Claim Value Distribution + Monte Carlo Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Claim Value Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Claim Value Distribution
            </h3>
            {data.claim_value_distribution && data.claim_value_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.claim_value_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Claims" fill="#22c55e" radius={[4, 4, 0, 0]}>
                    {data.claim_value_distribution.map((_, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-gray-500">
                No distribution data available
              </div>
            )}
          </div>

          {/* Monte Carlo Aggregate Stats */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Monte Carlo Analysis Overview
            </h3>
            {data.monte_carlo_aggregate ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-purple-700">
                      {data.monte_carlo_aggregate.claims_with_mc}
                    </div>
                    <div className="text-xs text-purple-600 mt-1">Claims with MC Analysis</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-blue-700">
                      {data.monte_carlo_aggregate.avg_ci_width_pct}%
                    </div>
                    <div className="text-xs text-blue-600 mt-1">Avg CI Width (% of P50)</div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4">
                  <div className="text-sm text-gray-600 mb-2">Average P50 (Median Estimate)</div>
                  <div className="text-2xl font-bold text-green-700">
                    {formatCurrency(data.monte_carlo_aggregate.avg_p50)}
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="text-sm text-gray-600 mb-2">Aggregate 95% Confidence Range</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Low (2.5%)</div>
                      <div className="text-lg font-semibold text-gray-700">
                        {formatCurrency(data.monte_carlo_aggregate.total_ci_low)}
                      </div>
                    </div>
                    <div className="text-gray-400">—</div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">High (97.5%)</div>
                      <div className="text-lg font-semibold text-gray-700">
                        {formatCurrency(data.monte_carlo_aggregate.total_ci_high)}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 text-center">
                  Monte Carlo simulations provide statistical confidence bounds
                </div>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-gray-500">
                No Monte Carlo data available
              </div>
            )}
          </div>
        </div>

        {/* Extraction Pipeline Analytics Section */}
        <div className="mt-8">
          <ExtractionAnalytics days={30} />
        </div>
        
        {/* Vendor Classification Cost Savings Over Time */}
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-6 h-6 text-green-600" />
              Vendor Classification Intelligence
            </h2>
            <p className="text-gray-500 text-sm mt-1">Historical performance of multi-tier vendor classification system</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Classification Tier Trend Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Classification Tier Distribution (6 Months)
              </h3>
              {vendorHistoryLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : vendorHistory?.monthly_data?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={vendorHistory.monthly_data} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }} 
                        tickFormatter={(val) => {
                          const [year, month] = val.split('-');
                          return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short' });
                        }}
                      />
                      <YAxis tickFormatter={(val) => `${val}%`} domain={[0, 100]} />
                      <Tooltip 
                        formatter={(value: number, name: string) => {
                          const labels: Record<string, string> = {
                            'tier_1_rate': 'Tier 1 (Local DB)',
                            'tier_2_rate': 'Tier 2 (Local ML)',
                            'tier_3_rate': 'Tier 3 (AI API)'
                          };
                          return [`${value}%`, labels[name] || name];
                        }}
                      />
                      <Legend 
                        formatter={(value) => {
                          const labels: Record<string, string> = {
                            'tier_1_rate': 'Tier 1 - Local DB (Free)',
                            'tier_2_rate': 'Tier 2 - ML (Free)',
                            'tier_3_rate': 'Tier 3 - AI API (Paid)'
                          };
                          return labels[value] || value;
                        }}
                      />
                      <Bar dataKey="tier_1_rate" stackId="a" fill="#22c55e" name="tier_1_rate" />
                      <Bar dataKey="tier_2_rate" stackId="a" fill="#3b82f6" name="tier_2_rate" />
                      <Bar dataKey="tier_3_rate" stackId="a" fill="#f59e0b" name="tier_3_rate" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-6 mt-3 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-green-500"></span>
                      Tier 1 - Free (Local DB)
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-blue-500"></span>
                      Tier 2 - Free (ML)
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-amber-500"></span>
                      Tier 3 - Paid (AI API)
                    </span>
                  </div>
                </>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No classification history available
                </div>
              )}
            </div>
            
            {/* Cost Savings Trend */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Cumulative Cost Savings
              </h3>
              {vendorHistoryLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : vendorHistory?.monthly_data?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={vendorHistory.monthly_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(val) => {
                          const [year, month] = val.split('-');
                          return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short' });
                        }}
                      />
                      <YAxis tickFormatter={(val) => `$${val}`} />
                      <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cumulative Savings']} />
                      <Line 
                        type="monotone" 
                        dataKey="cumulative_savings" 
                        stroke="#22c55e" 
                        strokeWidth={3}
                        dot={{ fill: '#22c55e', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-green-700">
                        ${vendorHistory.summary?.total_cumulative_savings?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-xs text-green-600">Total Saved</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-blue-700">
                        {vendorHistory.summary?.avg_free_rate?.toFixed(0) || 0}%
                      </div>
                      <div className="text-xs text-blue-600">Avg Free Rate</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-purple-700">
                        {(vendorHistory.summary?.database_vendors || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-purple-600">Vendors in DB</div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">
                      💡 As the local vendor database grows, more transactions are classified for <strong>free</strong> via Tier 1
                    </p>
                  </div>
                </>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-500">
                  No savings data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Email Engagement Analytics Section */}
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="w-6 h-6 text-purple-600" />
              Client Email Engagement
            </h2>
            <p className="text-gray-500 text-sm mt-1">Track open rates, click rates, and client response to evidence requests</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Key Metrics Cards */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Email Performance
              </h3>
              {emailAnalyticsLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : emailAnalytics?.summary ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-gray-600">Total Sent</span>
                    </div>
                    <span className="text-2xl font-bold text-purple-700">{emailAnalytics.summary.total_sent || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-gray-600">Open Rate</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-700">{emailAnalytics.summary.open_rate_percent || 0}%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <MousePointer className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-gray-600">Click Rate</span>
                    </div>
                    <span className="text-2xl font-bold text-green-700">{emailAnalytics.summary.click_rate_percent || 0}%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-amber-600" />
                      <span className="text-sm text-gray-600">Click-to-Open</span>
                    </div>
                    <span className="text-2xl font-bold text-amber-700">{emailAnalytics.summary.click_to_open_percent || 0}%</span>
                  </div>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-500 text-sm">
                  No email data available yet
                </div>
              )}
            </div>
            
            {/* Engagement Trend Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Email Engagement Over Time
              </h3>
              {emailAnalyticsLoading ? (
                <div className="h-[250px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : emailAnalytics?.daily_trend?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={emailAnalytics.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        formatter={(value: number, name: string) => {
                          const labels: Record<string, string> = {
                            'sent': 'Emails Sent',
                            'opened': 'Opened',
                            'clicked': 'Clicked'
                          };
                          return [value, labels[name] || name];
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="sent" stroke="#8b5cf6" name="sent" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="opened" stroke="#3b82f6" name="opened" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="clicked" stroke="#22c55e" name="clicked" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-6 mt-2 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-purple-500"></span>
                      Sent
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-blue-500"></span>
                      Opened
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded bg-green-500"></span>
                      Clicked
                    </span>
                  </div>
                </>
              ) : (
                <div className="h-[250px] flex flex-col items-center justify-center text-gray-500">
                  <Mail className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-sm">No email engagement data yet</p>
                  <p className="text-xs text-gray-400 mt-1">Send evidence requests to start tracking</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Recent Email Activity */}
          {emailAnalytics?.recent_emails?.length > 0 && (
            <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                Recent Email Activity
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="email-activity-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Recipient</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Sent</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Opened</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailAnalytics.recent_emails.slice(0, 10).map((email: any, idx: number) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className="font-medium text-gray-900">{email.to_email}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                            {email.email_type?.replace('_', ' ') || 'evidence request'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {email.sent_at ? new Date(email.sent_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {email.open_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-blue-600">
                              <Eye className="w-4 h-4" />
                              {email.open_count}x
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {email.click_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <MousePointer className="w-4 h-4" />
                              {email.click_count}x
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* A/B Test Results Section */}
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-indigo-600" />
              Email A/B Test Results
            </h2>
            <p className="text-gray-500 text-sm mt-1">Compare subject line and CTA button performance to optimize engagement</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Subject Line Test */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Subject Line Test
              </h3>
              <p className="text-xs text-gray-500 mb-4">Optimizing for: Open Rate</p>
              
              {abTestLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : abTestResults?.results?.subject_line?.variants?.length > 0 ? (
                <div className="space-y-3">
                  {/* Locked Winner Banner */}
                  {lockedWinners?.subject_line && (
                    <div className="flex items-center justify-between p-3 bg-green-100 border border-green-300 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-green-700" />
                        <span className="text-sm text-green-800">
                          Locked to variant <strong>{lockedWinners.subject_line.variant_id}</strong> ({lockedWinners.subject_line.variant_name})
                        </span>
                      </div>
                      <button
                        onClick={() => clearWinner('subject_line')}
                        disabled={applyingWinner === 'clear_subject_line'}
                        className="px-2 py-1 text-xs text-green-700 hover:bg-green-200 rounded transition-colors flex items-center gap-1"
                        data-testid="clear-subject-winner-btn"
                      >
                        {applyingWinner === 'clear_subject_line' ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Unlock className="w-3 h-3" />
                        )}
                        Resume Testing
                      </button>
                    </div>
                  )}
                  
                  {abTestResults.results.subject_line.variants.map((variant: any, idx: number) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border ${variant.is_winner ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            variant.id === 'A' ? 'bg-blue-100 text-blue-700' :
                            variant.id === 'B' ? 'bg-purple-100 text-purple-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {variant.id}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{variant.name}</span>
                          {variant.is_winner && (
                            <Trophy className="w-4 h-4 text-green-600" />
                          )}
                          {lockedWinners?.subject_line?.variant_id === variant.id && (
                            <Lock className="w-3 h-3 text-green-600" />
                          )}
                        </div>
                        {/* Apply Winner Button - show only if has enough data and not already locked to this */}
                        {variant.sent >= 10 && !lockedWinners?.subject_line && (
                          <button
                            onClick={() => applyWinner('subject_line', variant.id)}
                            disabled={applyingWinner === `subject_line_${variant.id}`}
                            className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded transition-colors flex items-center gap-1"
                            data-testid={`apply-subject-${variant.id}-btn`}
                          >
                            {applyingWinner === `subject_line_${variant.id}` ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Lock className="w-3 h-3" />
                            )}
                            Apply
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">Sent: <strong>{variant.sent}</strong></span>
                        <span className="text-blue-600 flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {variant.open_rate_percent}%
                        </span>
                        <span className="text-green-600 flex items-center gap-1">
                          <MousePointer className="w-3 h-3" />
                          {variant.click_rate_percent}%
                        </span>
                      </div>
                      {/* Progress bar for open rate */}
                      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${variant.is_winner ? 'bg-green-500' : 'bg-blue-400'}`}
                          style={{ width: `${Math.min(variant.open_rate_percent, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Total emails: {abTestResults.results.subject_line.total_emails}
                  </p>
                </div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-gray-500">
                  <FlaskConical className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-sm">No A/B test data yet</p>
                  <p className="text-xs text-gray-400 mt-1">Tests run automatically on each email</p>
                </div>
              )}
            </div>
            
            {/* CTA Button Test */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MousePointer className="w-5 h-5 text-green-600" />
                CTA Button Test
              </h3>
              <p className="text-xs text-gray-500 mb-4">Optimizing for: Click Rate</p>
              
              {abTestLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : abTestResults?.results?.cta_button?.variants?.length > 0 ? (
                <div className="space-y-3">
                  {/* Locked Winner Banner */}
                  {lockedWinners?.cta_button && (
                    <div className="flex items-center justify-between p-3 bg-green-100 border border-green-300 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-green-700" />
                        <span className="text-sm text-green-800">
                          Locked to variant <strong>{lockedWinners.cta_button.variant_id}</strong> ({lockedWinners.cta_button.variant_name})
                        </span>
                      </div>
                      <button
                        onClick={() => clearWinner('cta_button')}
                        disabled={applyingWinner === 'clear_cta_button'}
                        className="px-2 py-1 text-xs text-green-700 hover:bg-green-200 rounded transition-colors flex items-center gap-1"
                        data-testid="clear-cta-winner-btn"
                      >
                        {applyingWinner === 'clear_cta_button' ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Unlock className="w-3 h-3" />
                        )}
                        Resume Testing
                      </button>
                    </div>
                  )}
                  
                  {abTestResults.results.cta_button.variants.map((variant: any, idx: number) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border ${variant.is_winner ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            variant.id === 'A' ? 'bg-blue-100 text-blue-700' :
                            variant.id === 'B' ? 'bg-purple-100 text-purple-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {variant.id}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{variant.name}</span>
                          {variant.is_winner && (
                            <Trophy className="w-4 h-4 text-green-600" />
                          )}
                          {lockedWinners?.cta_button?.variant_id === variant.id && (
                            <Lock className="w-3 h-3 text-green-600" />
                          )}
                        </div>
                        {/* Apply Winner Button */}
                        {variant.sent >= 10 && !lockedWinners?.cta_button && (
                          <button
                            onClick={() => applyWinner('cta_button', variant.id)}
                            disabled={applyingWinner === `cta_button_${variant.id}`}
                            className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded transition-colors flex items-center gap-1"
                            data-testid={`apply-cta-${variant.id}-btn`}
                          >
                            {applyingWinner === `cta_button_${variant.id}` ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Lock className="w-3 h-3" />
                            )}
                            Apply
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">Sent: <strong>{variant.sent}</strong></span>
                        <span className="text-blue-600 flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {variant.open_rate_percent}%
                        </span>
                        <span className="text-green-600 flex items-center gap-1">
                          <MousePointer className="w-3 h-3" />
                          {variant.click_rate_percent}%
                        </span>
                      </div>
                      {/* Progress bar for click rate */}
                      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${variant.is_winner ? 'bg-green-500' : 'bg-green-400'}`}
                          style={{ width: `${Math.min(variant.click_rate_percent * 2, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Total emails: {abTestResults.results.cta_button.total_emails}
                  </p>
                </div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-gray-500">
                  <FlaskConical className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-sm">No A/B test data yet</p>
                  <p className="text-xs text-gray-400 mt-1">Tests run automatically on each email</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
            <p className="text-sm text-indigo-800">
              <strong>💡 How it works:</strong> Each evidence request email randomly receives one of three subject lines and one of three CTA button texts. 
              Performance is tracked via open/click rates. The winner is determined when a variant shows statistically significant improvement (min 10 emails).
            </p>
          </div>
        </div>

        {/* Email Retry Queue Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <RotateCcw className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Email Retry Queue</h2>
                <p className="text-sm text-gray-500">Automatic retry for failed email deliveries</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchRetryStats}
                disabled={retryLoading}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${retryLoading ? 'animate-spin' : ''}`} />
              </button>
              {retryStats?.due_now > 0 && (
                <button
                  onClick={processRetries}
                  disabled={processingRetries}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 text-sm font-medium"
                >
                  <Play className="w-4 h-4" />
                  {processingRetries ? 'Processing...' : `Process ${retryStats.due_now} Now`}
                </button>
              )}
            </div>
          </div>
          
          {retryLoading ? (
            <div className="h-48 flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-gray-300 animate-spin" />
            </div>
          ) : retryStats ? (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700">Pending</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-800">{retryStats.total_pending || 0}</p>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">Due Now</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-800">{retryStats.due_now || 0}</p>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-medium text-green-700">Delivered</span>
                  </div>
                  <p className="text-2xl font-bold text-green-800">{retryStats.total_delivered || 0}</p>
                </div>
                
                <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <div className="flex items-center gap-2 mb-1">
                    <XOctagon className="w-4 h-4 text-red-600" />
                    <span className="text-xs font-medium text-red-700">Failed</span>
                  </div>
                  <p className="text-2xl font-bold text-red-800">{retryStats.total_failed || 0}</p>
                </div>
                
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-medium text-indigo-700">Success Rate (7d)</span>
                  </div>
                  <p className="text-2xl font-bold text-indigo-800">{retryStats.success_rate_7d || 0}%</p>
                </div>
              </div>
              
              {/* Recent 24h Failures Alert */}
              {retryStats.recent_failures_24h > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">
                    <strong>{retryStats.recent_failures_24h}</strong> email(s) permanently failed in the last 24 hours after exhausting all retries.
                  </p>
                </div>
              )}
              
              {/* Retry History Table */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Retry Jobs</h3>
                {retryHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Email To</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Retries</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Next Retry</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {retryHistory.map((job: any) => (
                          <tr key={job._id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3">
                              <span className="text-gray-800 truncate max-w-[150px] block">{job.to_email}</span>
                            </td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                                {job.email_type?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                job.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                job.status === 'failed' ? 'bg-red-100 text-red-700' :
                                job.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                job.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {job.status}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-600">
                              {job.retry_count || 0} / {job.max_retries || 4}
                            </td>
                            <td className="py-2 px-3 text-gray-600">
                              {job.status === 'pending' && job.next_retry_at ? (
                                <span className="text-xs">
                                  {new Date(job.next_retry_at).toLocaleString()}
                                </span>
                              ) : job.status === 'delivered' && job.delivered_at ? (
                                <span className="text-green-600 text-xs">
                                  ✓ {new Date(job.delivered_at).toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {job.status === 'pending' && (
                                <button
                                  onClick={() => cancelRetry(job._id)}
                                  disabled={cancellingRetry === job._id}
                                  className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                >
                                  {cancellingRetry === job._id ? '...' : 'Cancel'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm">No retry jobs yet</p>
                    <p className="text-xs text-gray-400 mt-1">Failed emails will automatically appear here</p>
                  </div>
                )}
              </div>
              
              {/* How it works info */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-sm text-amber-800">
                  <strong>🔄 How it works:</strong> When an email fails to send, it's automatically queued for retry with exponential backoff (5min → 15min → 1hr → 4hr). 
                  After 4 failed attempts, the email is marked as permanently failed. Retries are processed every 5 minutes by the background scheduler.
                </p>
              </div>
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-500">
              <RotateCcw className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-sm">Unable to load retry stats</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
